import { useCallback, useRef, useState } from 'react';

/**
 * Leegality Web SDK v8 hook.
 *
 * Handles browser popup blocking by splitting the flow into two steps:
 * 1. `initSigningPopup(callbacks)` — call this SYNCHRONOUSLY inside the user's
 *    click handler to open the blank popup (browsers allow this).
 * 2. `startEsign(url)` — call this AFTER async work (API calls, validation)
 *    to navigate the already-open popup to the signing URL.
 *
 * If the sign URL is already available (no async needed), use the single-step
 * `openSigningPopup(url, callbacks)` convenience method.
 *
 * SDK reference: https://gitlab.leegality.com/leegality-public/web-sdk
 */

export interface LeegalityEsignCallbacks {
  /** Called when signing completes successfully. Receives message and documentId. */
  onSuccess?: (response: { message: string; documentId: string }) => void;
  /** Called when an error occurs or user closes the popup */
  onError?: (error: string) => void;
}

export interface LeegalityEsignOptions {
  /** Optional logo URL shown on the loading screen before signing page appears */
  logoUrl?: string;
}

export function useLeegalityEsign(options?: LeegalityEsignOptions) {
  const [isSigningOpen, setIsSigningOpen] = useState(false);
  const leegalityRef = useRef<Leegality | null>(null);
  const callbacksRef = useRef<LeegalityEsignCallbacks>({});

  /**
   * Step 1: Call this SYNCHRONOUSLY in the click handler.
   * Opens the popup immediately so it won't be blocked by the browser.
   * Returns true if popup was opened successfully, false otherwise.
   */
  const initSigningPopup = useCallback(
    (callbacks?: LeegalityEsignCallbacks): boolean => {
      if (isSigningOpen) {
        console.warn('Leegality signing session already in progress.');
        return false;
      }

      const LeegalityClass = window.Leegality;
      if (!LeegalityClass) {
        console.error('Leegality SDK not loaded.');
        return false;
      }

      callbacksRef.current = callbacks || {};
      setIsSigningOpen(true);

      try {
        const leegality = new LeegalityClass({
          logoUrl: options?.logoUrl,
          callback: (response) => {
            setIsSigningOpen(false);
            leegalityRef.current = null;

            if (response.error) {
              console.error('Leegality signing error:', response.error);
              callbacksRef.current.onError?.(response.error);
            } else {
              callbacksRef.current.onSuccess?.({
                message: response.message || '',
                documentId: response.documentId || '',
              });
            }
          },
        });

        leegalityRef.current = leegality;
        leegality.init();
        return true;
      } catch (error) {
        setIsSigningOpen(false);
        leegalityRef.current = null;
        console.error('Failed to initialize Leegality SDK:', error);
        callbacksRef.current.onError?.(String(error));
        return false;
      }
    },
    [isSigningOpen, options?.logoUrl]
  );

  /**
   * Step 2: Call this AFTER async work completes.
   * Navigates the already-open popup to the signing URL.
   */
  const startEsign = useCallback((signUrl: string) => {
    if (!leegalityRef.current) {
      console.error('Popup not initialized. Call initSigningPopup() first.');
      // Fallback: open in new tab
      window.open(signUrl, '_blank');
      return;
    }
    leegalityRef.current.esign(signUrl);
  }, []);

  /**
   * Single-step convenience method.
   * Use ONLY when the sign URL is already available (no async needed).
   * Must still be called synchronously within a user click handler.
   */
  const openSigningPopup = useCallback(
    (signUrl: string, callbacks?: LeegalityEsignCallbacks) => {
      const opened = initSigningPopup(callbacks);
      if (opened) {
        startEsign(signUrl);
      } else {
        // Fallback to new tab
        window.open(signUrl, '_blank');
      }
    },
    [initSigningPopup, startEsign]
  );

  /** Cancel the signing process and close the popup */
  const cancelSigning = useCallback(() => {
    if (leegalityRef.current) {
      leegalityRef.current.cancel();
      leegalityRef.current = null;
      setIsSigningOpen(false);
    }
  }, []);

  return {
    initSigningPopup,
    startEsign,
    openSigningPopup,
    cancelSigning,
    isSigningOpen,
  };
}
